import { Box, Breadcrumbs, Button, LinearProgress, Typography } from '@mui/material';
import { CustomerManagementTable } from './tables/customer-management-table';
import { useCustomerManagementTableInstance } from './tables/customer-management-table-instance';
import { CustomerManagementGridFilters } from './customer-management-grid-filter';
import { useQueryCustomerWithFilters } from 'shared/queries';
import { NewCustomerDialog } from './new-customer-dialog';
import { useToggle, useUserPermissions } from 'shared/hooks';
import { useMemo, useState } from 'react';
import {
  buttonContainerStyles,
  headerContentStyles,
  mainContentStyles,
  tableContainerStyles,
  tableContentStyles,
} from './styles';
import { useService } from 'shared/providers';
import { useAuthState } from 'shared/stores';
import { getControllerUserBrand } from 'shared/utility';

export function CustomerManagement() {
  const { appConfig } = useService();
  const authState = useAuthState();
  const user = authState.user.get();
  const userPermissions = useUserPermissions();
  const hasControllerPermission = userPermissions.includes('abg-retail.controller');
  const environment = appConfig.environment;

  const userBrands = useMemo(() =>
    hasControllerPermission ?
      getControllerUserBrand(environment, user) :
      null,
    [environment, user, hasControllerPermission]);
  const { customers, isFetching, isLoading } = useQueryCustomerWithFilters(userBrands);
  const loading = isFetching || isLoading;
  const customerManagementTableInstance = useCustomerManagementTableInstance(customers ?? []);
  const [newCustomerDialogOpen, newCustomerToggleDialog] = useToggle();
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);

  const openNewCustomerDialog = () => {
    newCustomerToggleDialog();
    setIsExistingCustomer(false);
  };

  const openAddToExistingCustomerDialog = () => {
    newCustomerToggleDialog();
    setIsExistingCustomer(true);
  };

  return (
    <Box sx={mainContentStyles}>
      <Box sx={headerContentStyles}>
        <Breadcrumbs separator='>' aria-label='customer overview'>
          <Typography color='text.primary'>Customer Management</Typography>
        </Breadcrumbs>
        {
          !hasControllerPermission &&
          <>
            <Box sx={buttonContainerStyles}>
              <Button disabled={loading} variant='outlined' color='secondary' onClick={openAddToExistingCustomerDialog}>
                Add To Existing Customer
              </Button>
              <Button disabled={loading} variant='contained' onClick={openNewCustomerDialog}>
                New Customer
              </Button>
            </Box>
            <NewCustomerDialog
              isExisting={isExistingCustomer}
              isOpen={newCustomerDialogOpen}
              toggleDialog={newCustomerToggleDialog}
              customers={customers || []}
            />
          </>
        }
      </Box>
      <Box sx={tableContentStyles}>
        <CustomerManagementGridFilters tableInstance={customerManagementTableInstance} />
        <Box sx={tableContainerStyles}>
          {loading && <LinearProgress color='secondary' />}
          <CustomerManagementTable instance={customerManagementTableInstance} />
        </Box>
      </Box>
    </Box>
  );
}
